/**
 * Super-admin token + registration-approval API wrappers (token billing).
 * Backed by routes/adminTokens.ts and the approve/reject endpoints in
 * routes/organizations.ts. All require a super-admin caller.
 */

import { apiRequest } from './api';

export interface TokenLedgerRow {
  id: string;
  type: 'grant' | 'debit' | 'adjustment';
  delta: number;
  balanceAfter: number;
  reason: string;
  allocationId?: string;
  executionId?: string;
  note?: string;
  createdAt?: any;
}

export interface OrgTokenInfo {
  orgId: string;
  plan: string | null;
  tokenBalance: number;
  ownDeviceTokenRate: number | null;
  ledger: TokenLedgerRow[];
}

export async function getOrgTokens(orgId: string): Promise<OrgTokenInfo> {
  return apiRequest<OrgTokenInfo>(`/admin/orgs/${orgId}/tokens`, { method: 'GET' });
}

export async function adjustOrgTokens(orgId: string, delta: number, note?: string): Promise<{ ok: boolean; balanceAfter: number }> {
  return apiRequest(`/admin/orgs/${orgId}/tokens/adjust`, {
    method: 'POST',
    body: JSON.stringify({ delta, note }),
  });
}

export async function setOwnDeviceRate(orgId: string, rate: number | null): Promise<{ ok: boolean; ownDeviceTokenRate: number | null }> {
  return apiRequest(`/admin/orgs/${orgId}/own-device-rate`, {
    method: 'PUT',
    body: JSON.stringify({ rate }),
  });
}

export async function approveRegistration(orgId: string): Promise<{ ok: boolean; approvalStatus: string }> {
  return apiRequest(`/organizations/${orgId}/approve-registration`, { method: 'POST' });
}

export async function rejectRegistration(orgId: string, reason?: string): Promise<{ ok: boolean; approvalStatus: string }> {
  return apiRequest(`/organizations/${orgId}/reject-registration`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export interface IssueInvoiceResult {
  ok: boolean;
  subscriptionId: string;
  invoiceId: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

export interface WireInvoiceResult {
  ok: boolean;
  invoiceId: string;
  reference: string;
  amountCents: number;
  currency: string;
  emailedTo?: string | null;
  orgId?: string;
}

/** Issue a wire invoice for an existing org (also converts a trial → wire). Paid by bank transfer via Wise. */
export async function issueWireInvoice(
  orgId: string,
  body: { plan: string; interval: 'month' | 'year'; amountUsd?: number; currency?: string; email?: string },
): Promise<WireInvoiceResult> {
  return apiRequest(`/admin/orgs/${orgId}/wire-invoice`, { method: 'POST', body: JSON.stringify(body) });
}

export interface InviteWireCustomerResult {
  ok: boolean;
  orgId: string;
  plan: string;
  interval: 'month' | 'year';
}

/**
 * Invite a brand-new org straight onto POSTPAID wire billing: creates account +
 * owner and activates the subscription immediately (no upfront invoice). The org
 * is invoiced in arrears at the end of each billing cycle. `amountUsd` is the
 * optional negotiated per-period price used for those invoices.
 */
export async function inviteWireCustomer(
  body: { email: string; name?: string; orgName?: string; plan: string; interval: 'month' | 'year'; amountUsd?: number; currency?: string },
): Promise<InviteWireCustomerResult> {
  return apiRequest(`/admin/wire-customers`, { method: 'POST', body: JSON.stringify(body) });
}

export interface CreateOrganizationResult {
  ok: boolean;
  orgId: string;
  /** true when an owner email was supplied → an invitation was emailed. */
  invited: boolean;
}

/**
 * Super-admin org creation. With `ownerEmail`, the owner is invited through the
 * standard registration flow (they accept terms on /signup) and attaches on accept;
 * without it, the caller owns the org with proper membership records written.
 */
export async function createOrganization(
  body: { name: string; plan?: string; status?: string; interval?: 'month' | 'year'; ownerEmail?: string },
): Promise<CreateOrganizationResult> {
  return apiRequest(`/admin/organizations`, { method: 'POST', body: JSON.stringify(body) });
}

/** @deprecated legacy Stripe send-invoice path — superseded by issueWireInvoice (Wise). */
export async function issueInvoice(
  orgId: string,
  body: { plan: string; interval?: 'month' | 'year'; daysUntilDue?: number; amountUsd?: number; email?: string },
): Promise<IssueInvoiceResult> {
  return apiRequest(`/admin/orgs/${orgId}/issue-invoice`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
